/**
 * Detailing1 — Sticky Buy Bar (2026-09), Stand: 2026-09-03
 *
 * Sichtbar, sobald der echte „In den Warenkorb"-Button nicht im Viewport steht.
 *
 * Das Banner hat KEIN eigenes Formular: der Kaufbutton klickt den Haupt-Button, die Mengenwahl
 * schreibt in das Mengenfeld des Hauptformulars. Die Variantenknoepfe sind reine Anker und
 * springen zum echten Variant-Picker — dort bleibt die Auswahl. Damit gibt es genau eine Wahrheit fuer Variante
 * und Menge, und der bestehende product-form-Ablauf (Cart-Drawer, Fehlermeldung, Vibration)
 * greift unveraendert. Beschriftung und Preis des Buttons werden vom Haupt-Button gespiegelt,
 * damit Vorbestell-Label und der Menge-mal-Preis aus atc-price.js automatisch mitkommen.
 *
 * Der Innenbereich traegt `data-dynamic-variant-content="sticky-atc"` und wird vom Variant-Picker
 * bei jedem Variantenwechsel ausgetauscht. Deshalb laeuft hier alles ueber Delegation statt ueber
 * gemerkte Element-Referenzen — die waeren nach dem ersten Wechsel tot.
 */
if (!customElements.get('d1-sticky-atc')) {
  class D1StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.section = this.closest('.shopify-section') || document;

      this.onClick = this.handleClick.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onMainQtyChange = this.syncFromMain.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onCartSettled = this.stopLoading.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onInput);
      document.addEventListener('on:variant:change', this.onVariantChange);
      ['on:cart:add', 'on:cart:error', 'on:line-item:change'].forEach((name) => {
        document.addEventListener(name, this.onCartSettled);
      });

      this.pastButton = false;
      // Beim Ueberfahren des Kaufbuttons wird zwangslaeufig nach unten gescrollt — deshalb
      // startet die Richtung auf „runter" und nicht auf „unbekannt".
      this.scrollDown = true;
      this.lastScrollPos = document.documentElement.scrollTop;
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.updateWidthMode.bind(this);
      this.wide = window.matchMedia('(min-width: 769px)');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.wide.addEventListener('change', this.onResize);

      this.hidden = false;
      this.trackHeight();
      this.updateWidthMode();
      this.observeFooter();
      this.observeModals();
      this.observeTarget();
      this.watchMainButton();
      this.syncFromMain();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.wide) this.wide.removeEventListener('change', this.onResize);
      document.removeEventListener('on:variant:change', this.onVariantChange);
      ['on:cart:add', 'on:cart:error', 'on:line-item:change'].forEach((name) => {
        document.removeEventListener(name, this.onCartSettled);
      });
      if (this.observer) this.observer.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
      if (this.modalWatcher) this.modalWatcher.disconnect();
      if (this.buttonWatcher) this.buttonWatcher.disconnect();
      if (this.sizeWatcher) this.sizeWatcher.disconnect();
      if (this.loadingTimer) clearTimeout(this.loadingTimer);
      if (this.boundQty) this.boundQty.removeEventListener('change', this.onMainQtyChange);
      document.documentElement.classList.remove('d1-has-satc-visible');
      document.documentElement.style.removeProperty('--d1-satc-h');
    }

    /** Das Hauptformular der Produktseite (nicht das der Schnellbestellung o. ae.). */
    get mainForm() {
      return this.section.querySelector('.js-product-form-main') || this.section.querySelector('.js-product-form');
    }

    /** Der echte Kaufbutton. Wird beim Variantenwechsel serverseitig ersetzt. */
    get mainButton() {
      const form = this.mainForm;
      return form ? form.querySelector('[name="add"]') : null;
    }

    /** Das Mengenfeld des Hauptformulars (sichtbar oder versteckt). */
    get mainQty() {
      const form = this.mainForm;
      return form ? form.querySelector('[name="quantity"]') : null;
    }

    get qtyInput() {
      return this.querySelector('[data-satc-qty]');
    }

    get button() {
      return this.querySelector('[data-satc-add]');
    }

    /** Haelt --d1-satc-h auf der tatsaechlichen Hoehe. */
    trackHeight() {
      const apply = () => {
        const height = this.offsetHeight;
        if (height) document.documentElement.style.setProperty('--d1-satc-h', `${height}px`);
      };

      apply();
      if ('ResizeObserver' in window) {
        this.sizeWatcher = new ResizeObserver(apply);
        this.sizeWatcher.observe(this);
      } else {
        window.addEventListener('resize', apply);
      }
    }

    /**
     * Im Footer soll das Banner weg — dort ist der Kunde nicht mehr im Kaufmodus, und die
     * Rechtstexte duerfen nicht verdeckt werden.
     */
    observeFooter() {
      const footer = document.querySelector('.footer');
      if (!footer || !('IntersectionObserver' in window)) return;

      if (this.footerObserver) this.footerObserver.disconnect();
      this.footerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inFooter = entry.isIntersecting;
          this.updateVisibility();
        });
      });
      this.footerObserver.observe(footer);
    }

    /**
     * Das Banner liegt ueber allem — auch ueber Cart-Drawer und Overlay. Deshalb weicht es,
     * sobald das Theme mit `body.fixed` eine Schublade, das Menue oder ein Dialogfenster oeffnet.
     */
    observeModals() {
      if (!('MutationObserver' in window)) return;
      const read = () => {
        const open = document.body.classList.contains('fixed');
        if (open === this.modalOpen) return;
        this.modalOpen = open;
        this.updateVisibility();
      };
      read();
      if (this.modalWatcher) this.modalWatcher.disconnect();
      this.modalWatcher = new MutationObserver(read);
      this.modalWatcher.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    /**
     * Spiegelt Beschriftung, Preis und Zustand des Haupt-Buttons. Der Zustand gehoert dem
     * Hauptformular — das Banner zeigt ihn nur an. So kommen Vorbestell-Label (atc-price.js),
     * „Ausverkauft" und der mit der Menge multiplizierte Preis ohne Doppellogik mit.
     */
    watchMainButton() {
      const main = this.mainButton;
      if (this.buttonWatcher) this.buttonWatcher.disconnect();
      if (!main) return;

      const mirror = () => {
        const button = this.button;
        if (!button) return;

        const label = button.querySelector('.d1-satc__btn-label');
        const mainLabel = main.querySelector('.js-atc-label');
        if (label && mainLabel && label.textContent !== mainLabel.textContent) {
          label.textContent = mainLabel.textContent;
        }

        const price = button.querySelector('.d1-satc__btn-price');
        const mainPrice = main.querySelector('.js-atc-price');
        if (price && mainPrice) {
          if (price.textContent !== mainPrice.textContent) price.textContent = mainPrice.textContent;
          price.hidden = mainPrice.hidden;
        }

        if (!this.isLoading) button.disabled = main.disabled;
      };

      mirror();
      if (!('MutationObserver' in window)) return;
      this.buttonWatcher = new MutationObserver(mirror);
      this.buttonWatcher.observe(main, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled', 'hidden'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    /**
     * Beobachtet den Kaufbutton — oder, wenn die Seite keinen hat (Templates ohne
     * Warenkorb), den Produktinfo-Block.
     */
    observeTarget() {
      if (!('IntersectionObserver' in window)) {
        this.pastButton = true;
        this.updateVisibility();
        return;
      }

      const target =
        this.section.querySelector('.product-info__add-to-cart') ||
        this.section.querySelector('.product-info');
      if (!target || target === this.observedTarget) return;

      if (this.observer) this.observer.disconnect();
      this.observedTarget = target;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Nur einblenden, wenn der Kaufbutton nach OBEN aus dem Bild gescrollt ist. Liegt er
          // noch darunter (der Kunde ist oben auf der Seite), waere das Banner nur im Weg.
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
          this.updateVisibility();
        });
      });
      this.observer.observe(target);
    }

    /**
     * Sichtbar nur, wenn der Kaufbutton nach oben aus dem Bild ist, gerade nach unten gescrollt
     * wird, der Footer noch nicht im Bild ist und der Kunde das Banner nicht weggeklickt hat.
     */
    updateVisibility() {
      // `is-eligible` = die Stelle waere richtig. Ob das Banner oder nur der kleine Kaufknopf
      // erscheint, entscheidet dann `is-dismissed`.
      // Zuklappen gibt es nur auf dem Handy — auf grossen Bildschirmen stoert das Banner nicht
      // genug, um dafuer eine Bedienung zu rechtfertigen.
      const eligible = this.pastButton && this.scrollDown && !this.inFooter && !this.modalOpen;
      const dismissed = Boolean(this.dismissed) && !(this.wide && this.wide.matches);
      const show = eligible && !dismissed;
      this.classList.toggle('is-eligible', eligible);
      this.classList.toggle('is-dismissed', dismissed);
      this.classList.toggle('is-visible', show);
      document.documentElement.classList.toggle('d1-has-satc-visible', show);
    }

    /**
     * Scrollrichtung — genau die Technik des klebenden Kopfs (store-header in main.js),
     * nur umgekehrt: der Kopf verschwindet beim Runterscrollen, dieses Banner erscheint dabei.
     * 5 px Hysterese gegen Zittern, 300 ms Sperre, damit nichts flattert.
     */
    handleScroll() {
      if (this.rafPending) return;
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        this.updateWidthMode();
        if (this.scrollTransitioning) return;

        const top = document.documentElement.scrollTop;
        if (this.lastScrollPos < top) {
          this.setScrollDown(true);
        } else if (this.lastScrollPos - 5 > top) {
          this.setScrollDown(false);
        }
        this.lastScrollPos = top;
      });
    }

    /**
     * Solange der Produktbereich noch ins Bild ragt, sitzt das Banner auf grossen Bildschirmen
     * buendig ueber der rechten Spalte — links bleibt die Galerie frei. Erst wenn der
     * Produktbereich durchgelaufen ist, geht es auf die volle Breite.
     */
    updateWidthMode() {
      if (!this.wide || !this.wide.matches) {
        this.classList.remove('is-aside');
        return;
      }

      // Bezug ist die Medienspalte, nicht `.product.js-product`: dessen Spalten sind gefloatet,
      // der Wrapper selbst hat Hoehe 0. Nur solange die Galerie bis auf Bannerhoehe herunterreicht,
      // wuerde ein Banner ueber die volle Breite links etwas verdecken.
      const media = this.section.querySelector('#product-media');
      const info = this.section.querySelector('.product-info');
      if (!info) return;

      const bannerTop = window.innerHeight - this.offsetHeight - 24;
      const aside = (media || info).getBoundingClientRect().bottom > bannerTop;
      this.classList.toggle('is-aside', aside);
      if (!aside) return;

      const rect = info.getBoundingClientRect();
      this.style.setProperty('--d1-satc-aside-left', `${Math.round(rect.left)}px`);
      this.style.setProperty('--d1-satc-aside-width', `${Math.round(rect.width)}px`);
    }

    /**
     * @param {Boolean} down - Es wird nach unten gescrollt.
     */
    setScrollDown(down) {
      if (this.scrollDown === down) return;
      this.scrollDown = down;
      this.scrollTransitioning = true;
      setTimeout(() => {
        this.scrollTransitioning = false;
        this.lastScrollPos = document.documentElement.scrollTop;
      }, 300);
      this.updateVisibility();
    }

    /**
     * Zuklappen — gilt nur fuer diese Seitenansicht. Nach einem Neuladen oder auf der naechsten
     * Seite ist das Banner wieder da; bewusst nichts gespeichert.
     */
    dismiss() {
      this.dismissed = true;
      this.updateVisibility();
    }

    /**
     * @param {object} evt - Klick-Ereignis.
     */
    handleClick(evt) {
      if (evt.target.closest('[data-satc-close]')) {
        evt.preventDefault();
        this.dismiss();
        return;
      }

      const jump = evt.target.closest('[data-satc-jump]');
      if (jump) {
        this.scrollToTitle(evt);
        return;
      }

      const step = evt.target.closest('[data-satc-step]');
      if (step) {
        evt.preventDefault();
        this.stepQty(parseInt(step.dataset.satcStep, 10));
        return;
      }

      const add = evt.target.closest('[data-satc-add]');
      if (!add || add.disabled) return;

      evt.preventDefault();
      const main = this.mainButton;
      if (!main || main.disabled) return;

      this.pushQty();
      this.startLoading();
      main.click();
    }

    /**
     * Springt so weit hoch, dass die Ueberschrift oben am Rand steht — knapp unter dem Kopf.
     * Der Anker `#product-info` im href bleibt als Rueckfallebene ohne JavaScript stehen; er
     * landet aber ueber der Ueberschrift, deshalb hier die genaue Rechnung.
     * @param {object} evt - Klick-Ereignis.
     */
    scrollToTitle(evt) {
      const title = this.section.querySelector('.product-info h1, h1.product-title');
      if (!title) return;

      evt.preventDefault();
      const styles = getComputedStyle(document.documentElement);
      const header =
        parseFloat(styles.getPropertyValue('--header-height'))
        || parseFloat(styles.getPropertyValue('--header-end'))
        || 0;

      window.scrollTo({
        top: Math.max(0, title.getBoundingClientRect().top + window.scrollY - header - 12),
        behavior: 'smooth'
      });
    }

    /**
     * Eigener Ladezustand: product-form.js haelt eine Referenz auf den Button, die nach einem
     * Variantenwechsel auf das ersetzte Element zeigt — die Klassen landen dann nicht mehr am
     * sichtbaren Button. Deshalb setzt die Leiste ihren Zustand selbst und raeumt ihn auf,
     * sobald der Warenkorb geantwortet hat (Sicherheitsnetz: 6 s).
     */
    startLoading() {
      const button = this.button;
      if (!button) return;
      this.isLoading = true;
      button.disabled = true;
      button.classList.add('is-loading');
      if (this.loadingTimer) clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => this.stopLoading(), 6000);
    }

    stopLoading() {
      if (!this.isLoading) return;
      this.isLoading = false;
      if (this.loadingTimer) clearTimeout(this.loadingTimer);
      const button = this.button;
      const main = this.mainButton;
      if (!button) return;
      button.classList.remove('is-loading');
      button.disabled = main ? main.disabled : false;
    }

    /**
     * @param {object} evt - Eingabe-Ereignis.
     */
    handleInput(evt) {
      if (!evt.target.matches('[data-satc-qty]')) return;
      this.pushQty();
    }

    /**
     * @param {Number} delta - Schrittweite.
     */
    stepQty(delta) {
      const input = this.qtyInput;
      if (!input) return;
      const current = Math.max(1, parseInt(input.value, 10) || 1);
      const next = Math.max(1, current + delta);
      if (next === current) return;
      input.value = next;
      this.pushQty();
    }

    /** Menge der Leiste in das Hauptformular schreiben. */
    pushQty() {
      const input = this.qtyInput;
      const main = this.mainQty;
      if (!input || !main) return;

      const value = Math.max(1, parseInt(input.value, 10) || 1);
      if (String(input.value) !== String(value)) input.value = value;
      if (String(main.value) === String(value)) return;

      main.value = value;
      main.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Menge aus dem Hauptformular uebernehmen (dort ist die Wahrheit). */
    syncFromMain() {
      const input = this.qtyInput;
      const main = this.mainQty;
      if (!main) return;
      if (input && String(input.value) !== String(main.value)) input.value = main.value;

      if (this.boundQty === main) return;
      if (this.boundQty) this.boundQty.removeEventListener('change', this.onMainQtyChange);
      this.boundQty = main;
      main.addEventListener('change', this.onMainQtyChange);
    }

    /**
     * Nach einem Variantenwechsel sind Kaufbutton, Mengenfeld und der Innenbereich der Leiste
     * frisch vom Server — alle Beobachter neu aufsetzen.
     */
    handleVariantChange() {
      requestAnimationFrame(() => {
        this.observedTarget = null;
        this.observeTarget();
        this.watchMainButton();
        this.syncFromMain();
      });
    }
  }

  customElements.define('d1-sticky-atc', D1StickyAtc);
}
